import { gql, useQuery } from '@apollo/client';
import IconEdit from '@apollo/icons/default/IconEdit.svg';
import IconError from '@apollo/icons/default/IconError.svg';
import {
  Button,
  Link,
  Modal,
  ModalBody,
  ModalContent,
  ModalDescription,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  ModalTitle,
} from '@apollo/orbit';
import { groupBy } from 'lodash';
import React from 'react';
import { useLocation } from 'react-router-dom';

import { ignorePermissionsErrors } from 'src/lib/apollo/catchErrors';
import { appLinkContext } from 'src/lib/apollo/link';
import Config from 'src/lib/config';
import { format } from 'src/lib/format';
import { GraphQLTypes } from 'src/lib/graphqlTypes';
import { setOverlayVisibleLocation } from 'src/lib/routing';

import { variantHomepageRouteConfig } from '../../routes';

const buildErrorModalQuery = gql`
  query LaunchErrorModalQuery(
    $graphId: ID!
    $graphVariant: String!
    $launchId: ID!
    $skipLatestLaunch: Boolean!
    $skipSpecificLaunch: Boolean!
  ) {
    service(id: $graphId) {
      id
      title
      variant(name: $graphVariant) {
        id
        name
        isContract
        permissions {
          canManageBuildConfig
        }
        sourceVariant {
          id
          name
        }
        latestLaunch @skip(if: $skipLatestLaunch) {
          id
          ...LaunchFragment
        }
        launch(id: $launchId) @skip(if: $skipSpecificLaunch) {
          id
          ...LaunchFragment
        }
      }
    }
  }

  fragment LaunchFragment on Launch {
    id
    build {
      result {
        ... on BuildFailure {
          errorMessages {
            code
            message
            locations {
              column
              line
            }
          }
        }
        ... on BuildSuccess {
          coreSchema {
            coreHash
          }
        }
      }
    }
  }
`;

export const LaunchErrorModal = ({
  isOpen,
  graphId,
  graphVariant,
  launchId,
  onClose,
}: {
  isOpen: boolean;
  graphId: string;
  graphVariant: string;
  launchId?: string;
  onClose: () => void;
}) => {
  const location = useLocation();

  const { data } = useQuery<
    GraphQLTypes.LaunchErrorModalQuery,
    GraphQLTypes.LaunchErrorModalQueryVariables
  >(buildErrorModalQuery, {
    variables: {
      graphId,
      graphVariant,
      launchId: launchId || '', // we have to pass *something*, even though we skip this part of the query if there's no launchId
      skipSpecificLaunch: !launchId,
      skipLatestLaunch: !!launchId,
    },
    context: appLinkContext({
      catchErrors: [ignorePermissionsErrors],
    }),
  });

  const latestResult = data?.service?.variant?.latestLaunch?.build?.result;
  const result = data?.service?.variant?.launch?.build?.result;
  const errors =
    (latestResult?.__typename === 'BuildFailure' &&
      latestResult.errorMessages) ||
    (result?.__typename === 'BuildFailure' && result.errorMessages) ||
    [];

  const errorGroups = groupBy(
    errors?.map((e) => ({
      message: e.message,
      code: e.code,
      group: data?.service?.variant?.id,
    })),
    (e) => e.group,
  );

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>
          <ModalTitle className="text-ellipsis">
            {data?.service?.variant?.id} has{' '}
            <span className="text-red">
              {errors?.length} {format.pluralize(errors?.length, 'build error')}
            </span>
          </ModalTitle>
          <ModalDescription>
            {data?.service?.variant?.isContract &&
            data?.service?.variant?.sourceVariant?.name ? (
              <>
                The following errors are preventing your contract from being
                built. To resolve, update your contract configuration or your
                source variant’s (
                <Link
                  to={variantHomepageRouteConfig.location({
                    graphId,
                    graphVariant: data.service.variant.sourceVariant.name,
                  })}
                >
                  {data.service.variant.sourceVariant.id}
                </Link>
                ) schema.
              </>
            ) : (
              <>
                Subgraph errors are preventing composition from occurring on
                your latest subgraph changes. Your API Schema will not contain
                these changes until the errors are resolved. You can push fixes
                from your deployment pipeline.
              </>
            )}
          </ModalDescription>
        </ModalHeader>
        <ModalBody>
          <div className="w-full flex-col items-center bg-red-lightest px-8 py-3">
            {Object.entries(errorGroups).map(([groupName, groupErrors]) => {
              return (
                <div
                  className="mb-1.5 mt-0.5 flex-col items-center"
                  key={groupName}
                >
                  {groupName && (
                    <div className="flex items-center">
                      <IconError className="text-icon-error [&_.inner]:fill-white" />
                      <span className="ml-2 text-base font-semibold text-red-darkest">
                        {groupName}
                      </span>
                    </div>
                  )}

                  {groupErrors.map(({ code, message }) => {
                    return (
                      <div
                        key={`${code}:${message}`}
                        className="my-2 ml-2 w-full flex-col justify-items-stretch border-l-2 border-red-lighter p-1 pl-4 font-mono text-xs text-red-darkest"
                      >
                        <p>{code}</p>
                        <p>{message}</p>
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </ModalBody>
        <ModalFooter>
          {data?.service?.variant?.isContract &&
            data?.service?.variant?.permissions.canManageBuildConfig && (
              <Link
                to={setOverlayVisibleLocation({
                  location,
                  overlayName: Config.modals.editContractConfigWizard,
                  additionalSearchParameters: {
                    [Config.queryParameters.ContractVariant]:
                      data?.service?.variant?.name,
                    [Config.queryParameters.SourceVariant]:
                      data.service.variant.sourceVariant?.name,
                  },
                })}
              >
                <IconEdit className="mr-2" />
                Edit Contract
              </Link>
            )}
          <Button variant="secondary" type="submit" onClick={onClose}>
            Done
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};
